class StickyNavController extends HTMLElement {
  constructor() {
    super();
    this.mainProductForm = null;
  }

  connectedCallback() {
    this.mainProductForm = document.querySelector("product-info");
    this.setupIntersectionObserver();
    this.attachEventListeners();
    this.listenForVariantChanges();
    // Run initial availability check after DOM is ready
    requestAnimationFrame(() => {
      this.updateAvailabilityFromCurrent();
      this._initTrayState();
      // Set sticky button mode based on the rendered initial variant state
      try {
        const selectedVariantEl = document.querySelector(
          "[data-selected-variant]",
        );
        if (selectedVariantEl) {
          const v = JSON.parse(selectedVariantEl.innerHTML);
          this._setStickyBtnMode(
            this._isPreorderVariant(v.id, v.available),
            v.available,
            v.price,
            v.id,
          );
          this._setTrayAtcBtnMode(
            this._isPreorderVariant(v.id, v.available),
            v.available,
            v.price,
            v.id,
          );
        }
      } catch (e) {}
    });
  }

  setupIntersectionObserver() {
    const addToCartSection = document.querySelector(".add-to-cart-section");
    if (!addToCartSection) return;

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    };

    // The bar may only show when the main add-to-cart form is out of view AND
    // the footer is out of view. We track each condition separately and let
    // _applyStickyVisibility() reconcile them so neither observer clobbers the
    // other's decision.
    this._atcInView = true;
    this._footerInView = false;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this._atcInView = entry.isIntersecting;
        this._applyStickyVisibility();
      });
    }, observerOptions);

    observer.observe(addToCartSection);

    // Hide the bar while the footer is on screen so it never overlaps the
    // footer. It rolls back up as soon as the footer scrolls away again.
    // (Emma-approved soft-collapse behaviour.)
    //
    // The theme can render more than one footer section (e.g. `.footer` and the
    // `.site_footer` custom footer), so observe every <footer> and treat the
    // bar as "footer in view" if ANY of them intersects.
    const footers = document.querySelectorAll("footer");
    if (footers.length) {
      this._footersInView = new Set();
      const footerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._footersInView.add(entry.target);
          } else {
            this._footersInView.delete(entry.target);
          }
        });
        this._footerInView = this._footersInView.size > 0;
        this._applyStickyVisibility();
      }, observerOptions);
      footers.forEach((footer) => footerObserver.observe(footer));
    }
  }

  _applyStickyVisibility() {
    // Visible only when the form is scrolled out of view and the footer hasn't
    // entered the viewport yet.
    const shouldShow = !this._atcInView && !this._footerInView;

    if (shouldShow) {
      // Show: add is-visible class (triggers slideUpIn animation).
      // Cancel any pending hide — rapid scrolling near the boundary used
      // to leave stale timeouts that stripped is-visible AFTER a re-show,
      // making the bar churn up/down ("run-away" header bug).
      clearTimeout(this._stickyHideTimer);
      this.classList.add("is-visible");
      this.classList.remove("is-hidden");
    } else {
      // Hide: add is-hidden class (triggers slideDownOut animation)
      this.classList.add("is-hidden");
      // Wait for animation to complete before removing is-visible
      clearTimeout(this._stickyHideTimer);
      this._stickyHideTimer = setTimeout(() => {
        this.classList.remove("is-visible");
      }, 400); // Match animation duration
    }
  }

  attachEventListeners() {
    // Size dropdown listener
    const sizeSelect = this.querySelector("#sticky-size-select");
    if (sizeSelect) {
      sizeSelect.addEventListener("change", (e) => this.onSizeChange(e));
    }

    // Color swatch listeners
    const colorSwatches = this.querySelectorAll(".sticky-nav-color-swatch");
    colorSwatches.forEach((swatch) => {
      swatch.addEventListener("click", (e) => this.onColorChange(e));
    });

    // Add to cart button listener
    const addToCartBtn = this.querySelector("#stickyAddToCartBtn");
    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", () => this.onAddToCart());
    }

    // Mobile variant tray
    this._setupTrayListeners();
  }

  onSizeChange(event) {
    const selectedSize = event.target.value;
    if (!selectedSize) return;

    // Try to delegate to the main variant-selects so product-info.js fires
    // the full update pipeline (price, URL, etc.)
    const mainVariantSelects = document.querySelector("variant-selects");
    if (mainVariantSelects) {
      this._pinScroll();
      const sizeInputs = mainVariantSelects.querySelectorAll(
        'input[type="radio"]',
      );
      let found = false;
      sizeInputs.forEach((input) => {
        if (input.value === selectedSize && !input.checked) {
          // Check that this input belongs to the size option, not colour
          const fieldset = input.closest("fieldset");
          const legend = fieldset
            ?.querySelector("legend")
            ?.textContent?.trim()
            .toLowerCase();
          if (legend && legend !== "colour" && legend !== "color") {
            input.checked = true;
            input.dispatchEvent(new Event("change", { bubbles: true }));
            found = true;
          }
        }
      });
      if (found) return;

      // Fallback for select-based size pickers
      const sizeSelects = mainVariantSelects.querySelectorAll("select");
      sizeSelects.forEach((select) => {
        const name = select.getAttribute("name") || "";
        if (
          !name.toLowerCase().includes("colour") &&
          !name.toLowerCase().includes("color")
        ) {
          if (select.value !== selectedSize) {
            const opt = Array.from(select.options).find(
              (o) => o.value === selectedSize,
            );
            if (opt) {
              select.value = selectedSize;
              select.dispatchEvent(new Event("change", { bubbles: true }));
              found = true;
            }
          }
        }
      });
      if (found) return;
    }

    // Fallback: only update availability UI if no main input found
    try {
      const variantsData = document.getElementById("ProductVariantsData");
      if (variantsData) {
        const variants = JSON.parse(variantsData.textContent);
        const activeColor =
          this.querySelector(".sticky-nav-color-swatch.active")?.getAttribute(
            "data-color",
          ) || null;
        this.updateAvailability(variants, activeColor, selectedSize);
      }
    } catch (e) {}
  }

  onColorChange(event) {
    event.preventDefault();
    const color = event.currentTarget.getAttribute("data-color");
    if (!color) return;

    // Update active state on sticky swatches
    this.querySelectorAll(".sticky-nav-color-swatch").forEach((s) =>
      s.classList.remove("active"),
    );
    event.currentTarget.classList.add("active");

    // Trigger the main variant-selects color input so product-info.js
    // fires optionValueSelectionChange and updates all page content
    const mainVariantSelects = document.querySelector("variant-selects");
    if (mainVariantSelects) {
      const colorInput = mainVariantSelects.querySelector(
        `input[value="${CSS.escape(color)}"]`,
      );
      if (colorInput && !colorInput.checked) {
        this._pinScroll();
        colorInput.click();
        return; // availability update will follow from the variant change
      }
    }

    // Fallback: only update availability UI when no main input found
    try {
      const variantsData = document.getElementById("ProductVariantsData");
      if (variantsData) {
        const variants = JSON.parse(variantsData.textContent);
        const currentSize =
          this.querySelector("#sticky-size-select")?.value || null;
        this.updateAvailability(variants, color, currentSize);
      }
    } catch (e) {}
  }

  resolveVariantId() {
    try {
      const variantsData = document.getElementById("ProductVariantsData");
      if (!variantsData) return null;
      const variants = JSON.parse(variantsData.textContent);

      // On mobile prefer the tray's selections (desktop center is hidden)
      const isMobile = window.matchMedia("(max-width: 768px)").matches;

      let selectedColor = null;
      let selectedSize = null;

      if (isMobile) {
        selectedColor =
          this.querySelector(".sticky-tray__color-option.active")?.dataset
            .color || null;
        selectedSize = this.querySelector("#sticky-tray-size")?.value || null;
      }

      // Fall back to desktop sticky nav selections
      if (!selectedColor) {
        selectedColor =
          this.querySelector(".sticky-nav-color-swatch.active")?.getAttribute(
            "data-color",
          ) || null;
      }
      if (!selectedSize) {
        selectedSize = this.querySelector("#sticky-size-select")?.value || null;
      }

      // Normalised compare — trailing spaces / case differences in option
      // values made in-stock variants resolve as "UNAVAILABLE".
      const norm = (s) => (s || "").trim().toLowerCase();

      // Match by both options when both are set
      if (selectedColor && selectedSize) {
        const match = variants.find(
          (v) =>
            norm(v.option1) === norm(selectedColor) &&
            norm(v.option2) === norm(selectedSize),
        );
        if (match) return match.id;
      }

      // Match by color only
      if (selectedColor) {
        const match = variants.find(
          (v) => norm(v.option1) === norm(selectedColor),
        );
        if (match) return match.id;
      }

      // Match by size only (normalised like the other matches)
      if (selectedSize) {
        const match = variants.find(
          (v) => norm(v.option2) === norm(selectedSize),
        );
        if (match) return match.id;
      }

      // Fallback: first available variant
      const available = variants.find((v) => v.available);
      return available ? available.id : variants[0]?.id || null;
    } catch (e) {
      return null;
    }
  }

  _isPreorderVariant(variantId, variantAvailable) {
    try {
      const mapEl = document.querySelector('[id^="preorder-data-"]');
      if (!mapEl) return false;
      const map = JSON.parse(mapEl.textContent);
      const entry = map[String(variantId)];
      if (!entry) return false;
      return entry.preorder === true && !variantAvailable;
    } catch (e) {
      return false;
    }
  }

  _formatMoney(cents, variantId) {
    if (!cents && cents !== 0) return "";
    if (variantId) {
      try {
        const mapEl = document.getElementById("ProductPricesData");
        if (mapEl) {
          const map = JSON.parse(mapEl.textContent);
          const formatted = map[String(variantId)];
          if (formatted) return formatted;
        }
      } catch (e) {}
    }
    // Fallback
    const dollars = cents / 100;
    const fmt = Number.isInteger(dollars)
      ? dollars.toString()
      : dollars.toFixed(2);
    return "$" + fmt;
  }

  _setStickyBtnMode(
    isPreorder,
    isAvailable = true,
    price = null,
    variantId = null,
  ) {
    const btn = this.querySelector("#stickyAddToCartBtn");
    if (!btn) return;
    if (isPreorder) {
      btn.disabled = false;
      btn.textContent = "PRE-ORDER";
      btn.dataset.mode = "preorder";
    } else if (!isAvailable) {
      // Mirror the PDP: an unavailable, non-preorder variant becomes a
      // NOTIFY ME prompt instead of a dead "OUT OF STOCK" button.
      btn.disabled = false;
      btn.textContent = "NOTIFY ME";
      btn.dataset.mode = "notify";
    } else {
      btn.disabled = false;
      const priceStr = price ? " - " + this._formatMoney(price, variantId) : "";
      btn.textContent = (btn.dataset.label || "ADD TO CART") + priceStr;
      delete btn.dataset.mode;
    }
  }

  onAddToCart() {
    const btn = this.querySelector("#stickyAddToCartBtn");

    // Preorder / notify mode: always scroll to the form (works on mobile and desktop)
    if (
      btn &&
      (btn.dataset.mode === "preorder" || btn.dataset.mode === "notify")
    ) {
      this._scrollToPreorderForm();
      return;
    }

    // On mobile: toggle the variant tray instead of adding to cart directly
    if (window.matchMedia("(max-width: 768px)").matches) {
      const tray = this.querySelector("#stickyVariantTray");
      if (tray) {
        if (tray.classList.contains("is-open")) {
          this.closeTray();
        } else {
          this.openTray();
        }
      }
      return;
    }

    // Desktop: add to cart directly
    this._performAddToCart(btn);
  }

  _scrollToPreorderForm() {
    const sId = document
      .querySelector('[id^="preorder-data-"]')
      ?.id.replace("preorder-data-", "");
    if (!sId) return;
    const inlineEl = document.getElementById("preorder-inline-" + sId);
    const openBtn = document.getElementById("preorder-open-" + sId);
    if (inlineEl && !inlineEl.classList.contains("is-open")) {
      inlineEl.hidden = false;
      requestAnimationFrame(() => inlineEl.classList.add("is-open"));
    }
    const target = inlineEl || openBtn;
    if (target) {
      this.closeTray();
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      const emailInput = document.getElementById("preorder-email-" + sId);
      if (emailInput)
        setTimeout(() => emailInput.focus({ preventScroll: true }), 400);
    }
  }

  _performAddToCart(callerBtn) {
    const btn = callerBtn || this.querySelector("#stickyAddToCartBtn");

    const variantId = this.resolveVariantId();

    if (!variantId) {
      if (btn) {
        btn.textContent = "SELECT OPTIONS";
        setTimeout(
          () => (btn.textContent = btn.dataset.label || "ADD TO CART"),
          2000,
        );
      }
      return;
    }

    if (btn) {
      btn.disabled = true;
      btn.textContent = "ADDING...";
    }

    // Use the same cart element the theme uses so renderContents() opens the drawer
    const cartEl =
      document.querySelector("cart-drawer") ||
      document.querySelector("cart-notification");

    // Build form data the same way product-form.js does
    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", 1);
    if (cartEl) {
      formData.append(
        "sections",
        cartEl
          .getSectionsToRender()
          .map((s) => s.id)
          .join(","),
      );
      formData.append("sections_url", window.location.pathname);
    }

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      body: formData,
    })
      .then((res) => res.json())
      .then(async (data) => {
        if (data.status) {
          if (btn) {
            btn.disabled = false;
            btn.textContent = data.description || "TRY AGAIN";
            setTimeout(
              () => (btn.textContent = btn.dataset.label || "ADD TO CART"),
              2500,
            );
          }
          return;
        }

        if (btn) {
          btn.textContent = "ADDED!";
          setTimeout(() => {
            btn.disabled = false;
            btn.textContent = btn.dataset.label || "ADD TO CART";
          }, 2000);
        }

        // Close tray after successful add on mobile
        this.closeTray();

        // Let the theme's cart element update its DOM and open the drawer/notification
        if (cartEl) {
          if (cartEl.classList.contains("is-empty"))
            cartEl.classList.remove("is-empty");
          cartEl.renderContents(data);
        }

        // Reconcile the complimentary wash-bag, then publish ONE cartUpdate so the
        // drawer re-renders with it added/removed. The sticky bar is the primary
        // mobile add path; without this it bypasses the complementary controller
        // entirely, so the wash-bag is never removed when the cart ends up holding
        // only excluded items (e.g. scrunchie, wellness bag).
        try {
          if (
            window.complementaryController &&
            window.complementaryController.addProduct
          ) {
            await window.complementaryController.addProduct(null, {
              silent: true,
            });
            if (
              typeof publish === "function" &&
              typeof PUB_SUB_EVENTS !== "undefined"
            ) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: "sticky-add" });
            }
          }
        } catch (e) {
          /* non-blocking */
        }
      })
      .catch(() => {
        if (btn) {
          btn.disabled = false;
          btn.textContent = "TRY AGAIN";
          setTimeout(
            () => (btn.textContent = btn.dataset.label || "ADD TO CART"),
            2500,
          );
        }
      });
  }

  openTray() {
    const tray = this.querySelector("#stickyVariantTray");
    if (!tray) return;
    // Ensure colour dropdown is closed when tray opens
    const colorSel = this.querySelector("#stickyTrayColorSelect");
    if (colorSel) colorSel.classList.remove("is-open");
    tray.style.overflow = "hidden"; // ensure clipped during open animation
    tray.classList.add("is-open");
    tray.setAttribute("aria-hidden", "false");
    // Once the slide-up animation finishes, allow the colour dropdown to overflow
    tray.addEventListener(
      "transitionend",
      () => {
        if (tray.classList.contains("is-open")) tray.style.overflow = "visible";
      },
      { once: true },
    );
  }

  closeTray() {
    const tray = this.querySelector("#stickyVariantTray");
    if (!tray) return;
    // Re-clip before the close animation so content doesn't escape
    tray.style.overflow = "hidden";
    tray.classList.remove("is-open");
    tray.setAttribute("aria-hidden", "true");
    const colorSel = this.querySelector("#stickyTrayColorSelect");
    if (colorSel) colorSel.classList.remove("is-open");
  }

  _initTrayState() {
    // Sync tray toggle swatch/name from the current active sticky nav colour swatch
    const activeNavSwatch = this.querySelector(
      ".sticky-nav-color-swatch.active",
    );
    if (activeNavSwatch) {
      const toggleSwatch = this.querySelector("#stickyTrayToggleSwatch");
      if (toggleSwatch && !toggleSwatch.style.background) {
        toggleSwatch.style.background = activeNavSwatch.style.backgroundColor;
      }
    }
  }

  _setupTrayListeners() {
    // Guard against double-init (attachEventListeners can be called multiple times)
    if (this._trayListenersAttached) return;
    this._trayListenersAttached = true;

    // Colour toggle open/close
    const colorToggle = this.querySelector("#stickyTrayColorToggle");
    const colorSel = this.querySelector("#stickyTrayColorSelect");
    if (colorToggle && colorSel) {
      colorToggle.addEventListener("click", (e) => {
        e.stopPropagation();
        const isOpen = colorSel.classList.toggle("is-open");
        colorToggle.setAttribute("aria-expanded", String(isOpen));
      });
    }

    // Colour option selection
    const colorOptions = this.querySelectorAll(".sticky-tray__color-option");
    colorOptions.forEach((opt) => {
      opt.addEventListener("click", () => {
        const color = opt.dataset.color;
        const bg = opt.dataset.colorBg;

        // Update toggle display (swatch morphs, name fades in)
        this._swapTrayToggle(color, bg);

        // Update active state on tray options
        colorOptions.forEach((o) => {
          o.classList.remove("active");
          o.setAttribute("aria-selected", "false");
        });
        opt.classList.add("active");
        opt.setAttribute("aria-selected", "true");

        // Close dropdown
        if (colorSel) {
          colorSel.classList.remove("is-open");
          if (colorToggle) colorToggle.setAttribute("aria-expanded", "false");
        }

        // Sync to main page variant-selects
        this._syncColorChange(color);
      });
    });

    // Size select
    const traySizeSelect = this.querySelector("#sticky-tray-size");
    if (traySizeSelect) {
      traySizeSelect.addEventListener("change", (e) => {
        this._syncSizeChange(e.target.value);
      });
    }

    // Tray ATC button
    const trayAtcBtn = this.querySelector("#stickyTrayAtcBtn");
    if (trayAtcBtn) {
      trayAtcBtn.addEventListener("click", () => {
        if (
          trayAtcBtn.dataset.mode === "preorder" ||
          trayAtcBtn.dataset.mode === "notify"
        ) {
          this._scrollToPreorderForm();
          return;
        }
        this._performAddToCart(trayAtcBtn);
      });
    }

    // Close colour dropdown when clicking outside
    document.addEventListener("click", (e) => {
      const cs = this.querySelector("#stickyTrayColorSelect");
      if (cs && !cs.contains(e.target)) {
        cs.classList.remove("is-open");
        const ct = this.querySelector("#stickyTrayColorToggle");
        if (ct) ct.setAttribute("aria-expanded", "false");
      }
    });

    // Close tray when clicking outside the sticky bar
    document.addEventListener("click", (e) => {
      const tray = this.querySelector("#stickyVariantTray");
      if (
        tray &&
        tray.classList.contains("is-open") &&
        !this.contains(e.target)
      ) {
        this.closeTray();
      }
    });
  }

  /**
   * Swap the floating colour toggle's swatch + name with a subtle transition.
   * The swatch morphs colour via CSS; the name does a nonchalant fade/slide-in.
   */
  _swapTrayToggle(name, bg) {
    const toggleSwatch = this.querySelector("#stickyTrayToggleSwatch");
    const toggleName = this.querySelector("#stickyTrayToggleName");

    if (toggleSwatch && bg) toggleSwatch.style.backgroundColor = bg;

    if (!toggleName) return;
    // No-op if the label isn't actually changing
    if (toggleName.textContent === name) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduceMotion || typeof toggleName.animate !== "function") {
      toggleName.textContent = name;
      return;
    }

    toggleName.textContent = name;
    toggleName.animate(
      [
        { opacity: 0, transform: "translateY(4px)" },
        { opacity: 1, transform: "translateY(0)" },
      ],
      { duration: 280, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }

  _syncColorChange(color) {
    // Update active state on desktop sticky colour swatches
    this.querySelectorAll(".sticky-nav-color-swatch").forEach((s) =>
      s.classList.remove("active"),
    );
    const matchingNavSwatch = this.querySelector(
      `.sticky-nav-color-swatch[data-color="${CSS.escape(color)}"]`,
    );
    if (matchingNavSwatch) matchingNavSwatch.classList.add("active");

    // Trigger the main variant-selects so product-info.js fires full update pipeline
    const mainVariantSelects = document.querySelector("variant-selects");
    if (mainVariantSelects) {
      const colorInput = mainVariantSelects.querySelector(
        `input[value="${CSS.escape(color)}"]`,
      );
      if (colorInput && !colorInput.checked) {
        this._pinScroll();
        colorInput.click();
        return;
      }
    }

    // Fallback: update availability UI only
    try {
      const variantsData = document.getElementById("ProductVariantsData");
      if (variantsData) {
        const variants = JSON.parse(variantsData.textContent);
        const currentSize =
          this.querySelector("#sticky-tray-size")?.value ||
          this.querySelector("#sticky-size-select")?.value ||
          null;
        this.updateAvailability(variants, color, currentSize);
      }
    } catch (e) {}
  }

  _syncSizeChange(size) {
    if (!size) return;

    // Keep desktop sticky size select in sync
    const stickySize = this.querySelector("#sticky-size-select");
    if (stickySize && stickySize.value !== size) stickySize.value = size;

    // Trigger main variant-selects
    const mainVariantSelects = document.querySelector("variant-selects");
    if (mainVariantSelects) {
      this._pinScroll();
      const sizeInputs = mainVariantSelects.querySelectorAll(
        'input[type="radio"]',
      );
      let found = false;
      sizeInputs.forEach((input) => {
        if (input.value === size && !input.checked) {
          const fieldset = input.closest("fieldset");
          const legend = fieldset
            ?.querySelector("legend")
            ?.textContent?.trim()
            .toLowerCase();
          if (legend && legend !== "colour" && legend !== "color") {
            input.checked = true;
            input.dispatchEvent(new Event("change", { bubbles: true }));
            found = true;
          }
        }
      });
      if (found) return;

      const sizeSelects = mainVariantSelects.querySelectorAll("select");
      sizeSelects.forEach((select) => {
        const name = select.getAttribute("name") || "";
        if (
          !name.toLowerCase().includes("colour") &&
          !name.toLowerCase().includes("color")
        ) {
          if (select.value !== size) {
            const opt = Array.from(select.options).find(
              (o) => o.value === size,
            );
            if (opt) {
              select.value = size;
              select.dispatchEvent(new Event("change", { bubbles: true }));
              found = true;
            }
          }
        }
      });
      if (found) return;
    }

    // Fallback
    try {
      const variantsData = document.getElementById("ProductVariantsData");
      if (variantsData) {
        const variants = JSON.parse(variantsData.textContent);
        const activeColor =
          this.querySelector(".sticky-tray__color-option.active")?.dataset
            .color ||
          this.querySelector(".sticky-nav-color-swatch.active")?.dataset
            .color ||
          null;
        this.updateAvailability(variants, activeColor, size);
      }
    } catch (e) {}
  }

  _setTrayAtcBtnMode(
    isPreorder,
    isAvailable = true,
    price = null,
    variantId = null,
  ) {
    const btn = this.querySelector("#stickyTrayAtcBtn");
    if (!btn) return;
    if (isPreorder) {
      btn.disabled = false;
      btn.textContent = "PRE-ORDER";
      btn.dataset.mode = "preorder";
    } else if (!isAvailable) {
      // Mirror the PDP: an unavailable, non-preorder variant becomes a
      // NOTIFY ME prompt instead of a dead "OUT OF STOCK" button.
      btn.disabled = false;
      btn.textContent = "NOTIFY ME";
      btn.dataset.mode = "notify";
    } else {
      btn.disabled = false;
      const priceStr = price ? " - " + this._formatMoney(price, variantId) : "";
      btn.textContent = (btn.dataset.label || "ADD TO CART") + priceStr;
      delete btn.dataset.mode;
    }
  }

  updateUI(variantId) {
    try {
      const variantsData = document.getElementById("ProductVariantsData");
      if (!variantsData) return;

      const variants = JSON.parse(variantsData.textContent);
      const selectedVariant = variants.find((v) => v.id === variantId);

      if (selectedVariant) {
        // Update size dropdown (desktop)
        if (selectedVariant.option2) {
          const sizeSelect = this.querySelector("#sticky-size-select");
          if (sizeSelect) {
            sizeSelect.value = selectedVariant.option2;
          }
          // Also sync tray size select
          const traySizeSelect = this.querySelector("#sticky-tray-size");
          if (traySizeSelect) traySizeSelect.value = selectedVariant.option2;
        }

        // Update color swatch (desktop)
        if (selectedVariant.option1) {
          const colorSwatches = this.querySelectorAll(
            ".sticky-nav-color-swatch",
          );
          colorSwatches.forEach((swatch) => swatch.classList.remove("active"));

          const activeSwatches = this.querySelectorAll(
            `.sticky-nav-color-swatch[data-color="${selectedVariant.option1}"]`,
          );
          activeSwatches.forEach((swatch) => swatch.classList.add("active"));

          // Also sync tray colour options + toggle
          const trayColorOpts = this.querySelectorAll(
            ".sticky-tray__color-option",
          );
          trayColorOpts.forEach((o) => {
            o.classList.remove("active");
            o.setAttribute("aria-selected", "false");
          });
          const activeTrayOpt = this.querySelector(
            `.sticky-tray__color-option[data-color="${selectedVariant.option1}"]`,
          );
          if (activeTrayOpt) {
            activeTrayOpt.classList.add("active");
            activeTrayOpt.setAttribute("aria-selected", "true");
            this._swapTrayToggle(
              selectedVariant.option1,
              activeTrayOpt.dataset.colorBg,
            );
          }
        }

        // Update availability
        this.updateAvailability(
          variants,
          selectedVariant.option1,
          selectedVariant.option2,
        );
      }
    } catch (e) {
      console.log("Error updating sticky nav UI:", e);
    }
  }

  updateAvailabilityFromCurrent() {
    try {
      const variantsData = document.getElementById("ProductVariantsData");
      if (!variantsData) return;
      const variants = JSON.parse(variantsData.textContent);

      const activeColor =
        this.querySelector(".sticky-nav-color-swatch.active")?.getAttribute(
          "data-color",
        ) || null;
      const activeSize =
        this.querySelector("#sticky-size-select")?.value || null;

      this.updateAvailability(variants, activeColor, activeSize);
    } catch (e) {
      console.log("Error in updateAvailabilityFromCurrent:", e);
    }
  }

  updateAvailability(variants, currentColor, currentSize) {
    // --- Color swatches ---
    const colorSwatches = this.querySelectorAll(".sticky-nav-color-swatch");
    colorSwatches.forEach((swatch) => {
      const swatchColor = swatch.getAttribute("data-color");
      let available;
      if (currentSize) {
        // Is there any available variant with this color AND the current size?
        available = variants.some(
          (v) =>
            v.option1 === swatchColor &&
            v.option2 === currentSize &&
            v.available,
        );
      } else {
        // No size selected — just check if any variant with this color is available
        available = variants.some(
          (v) => v.option1 === swatchColor && v.available,
        );
      }
      swatch.classList.toggle("unavailable", !available);
    });

    // --- Size dropdowns (desktop sticky nav + mobile tray) ---
    // Hide sizes with NO variant for the current colour (impossible combos), but
    // keep sold-out-yet-existing sizes visible (just disabled) so the rest of the
    // flow can still surface NOTIFY ME / PRE-ORDER for them.
    const norm = (s) => (s || "").trim().toLowerCase();
    const sizeSelects = [
      this.querySelector("#sticky-size-select"),
      this.querySelector("#sticky-tray-size"),
    ].filter(Boolean);
    sizeSelects.forEach((sizeSelect) => {
      Array.from(sizeSelect.options).forEach((option) => {
        if (!option.value) return; // skip placeholder
        const matchesColour = (v) =>
          !currentColor || norm(v.option1) === norm(currentColor);
        const exists = variants.some(
          (v) => matchesColour(v) && norm(v.option2) === norm(option.value),
        );
        const available = variants.some(
          (v) =>
            matchesColour(v) &&
            norm(v.option2) === norm(option.value) &&
            v.available,
        );
        option.hidden = !exists; // non-existent combo -> hide entirely
        option.disabled = exists && !available; // exists but sold out -> visible, disabled
      });
    });
  }

  /* ──────────────────────────────────────────────────────────────
     Scroll pin — no-jump variant change from the floating bar.

     Changing a variant from the sticky bar re-renders the product
     section (gallery, price, picker). That work happens ABOVE the
     fold while the customer is reading a section further down, so the
     reflow — plus the browser focusing the off-screen variant radio —
     used to shift or outright jump the page. We anchor to the section
     the customer is actually looking at the moment they pick a
     swatch/size and hold it in the same screen position until the
     re-render settles, so the change is invisible. Scrolling is
     briefly frozen during the swap (an approved trade-off) and freed
     again on settle.
     ────────────────────────────────────────────────────────────── */

  // The element crossing a reference line just below the header is what the
  // customer is looking at. Anchoring to THIS element (not a fixed pixel)
  // means any height change above the fold can't shift it. Returns null when
  // nothing suitable is found so the caller falls back to a fixed-pixel hold.
  _findScrollAnchor() {
    const refY = Math.min(Math.round(window.innerHeight * 0.35), 240);
    let el = document.elementFromPoint(Math.round(window.innerWidth / 2), refY);
    // Nodes INSIDE a per-color chunk are destroyed when its innerHTML is
    // replaced mid-change; the chunk container itself survives — anchor there.
    const chunk = el?.closest?.("[data-pdp-section]");
    if (chunk) el = chunk;
    while (el && el !== document.body && el !== document.documentElement) {
      const rect = el.getBoundingClientRect();
      // Skip trivially small inline nodes, the media gallery (its <li> get
      // re-ordered/replaced on variant change → unstable anchor) and the
      // variant picker (replaced wholesale by product-info.js).
      if (
        rect.height >= 24 &&
        !el.closest("media-gallery") &&
        !el.closest("variant-selects")
      )
        break;
      el = el.parentElement;
    }
    if (!el || el === document.body || el === document.documentElement)
      return null;
    if (el.closest("media-gallery")) return null;
    return el;
  }

  _pinScroll() {
    let anchorEl = this._findScrollAnchor();
    let anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;
    const fallbackY = window.scrollY;

    this._releaseScrollPin(); // cancel any in-flight pin first
    this._pinActive = true;

    // Hard-freeze the page for the duration of the swap: drop the scrollbar and
    // block all user scrolling so the change can't be nudged mid-flight. We pad
    // the right edge by the removed scrollbar width so the lock itself doesn't
    // shift the layout sideways. overflow:hidden only stops USER scrolling — the
    // rAF hold below still scrollTo()s to keep the anchored section pinned
    // against the content reflow.
    const docEl = document.documentElement;
    const bodyEl = document.body;
    if (!this._scrollLocked) {
      this._prevOverflow = docEl.style.overflow;
      this._prevBodyOverflow = bodyEl.style.overflow;
      this._prevPaddingRight = docEl.style.paddingRight;
      const scrollbarWidth = window.innerWidth - docEl.clientWidth;
      docEl.style.overflow = "hidden";
      bodyEl.classList.add("variant-changing");
      if (scrollbarWidth > 0) docEl.style.paddingRight = scrollbarWidth + "px";
      this._scrollLocked = true;
    }

    const hold = () => {
      if (!this._pinActive) {
        this._pinRAF = null;
        return;
      }
      if (anchorEl && !anchorEl.isConnected) {
        // The re-render destroyed the anchor node — re-anchor to whatever now
        // sits in the same viewport band. A fixed-pixel fallback can't
        // compensate above-fold height changes and lets content slide.
        anchorEl = this._findScrollAnchor();
        anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;
      }
      if (anchorEl && anchorEl.isConnected && anchorTop !== null) {
        const delta = anchorEl.getBoundingClientRect().top - anchorTop;
        if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
      } else if (Math.abs(window.scrollY - fallbackY) > 0.5) {
        window.scrollTo(0, fallbackY);
      }
      this._pinRAF = requestAnimationFrame(hold);
    };
    this._pinRAF = requestAnimationFrame(hold);

    // Safety net: the out-of-stock path re-renders (and reflows) but never
    // publishes variantChange, so _settleScrollPin() would never fire. Release
    // on a hard cap so scrolling can never stay frozen. Sized to outlast
    // fetch + the pre-swap image preload in product-info.js (capped at 900ms)
    // + the 800ms settle, so the pin can't release mid-swap on slow networks.
    this._pinHardStop = setTimeout(() => this._releaseScrollPin(), 3200);
  }

  // Called from the variantChange handler once the re-render has landed. Hold a
  // few extra frames to absorb late image reflow, then free scrolling again.
  _settleScrollPin() {
    if (!this._pinActive) return;
    clearTimeout(this._pinSettleTimer);
    this._pinSettleTimer = setTimeout(() => this._releaseScrollPin(), 800);
  }

  _releaseScrollPin() {
    this._pinActive = false;
    // Unfreeze the page — restore the scrollbar and the customer's ability to
    // scroll, undoing the scrollbar-width padding compensation.
    if (this._scrollLocked) {
      const docEl = document.documentElement;
      docEl.style.overflow = this._prevOverflow || "";
      document.body.classList.remove("variant-changing");
      docEl.style.paddingRight = this._prevPaddingRight || "";
      this._scrollLocked = false;
    }
    if (this._pinRAF) cancelAnimationFrame(this._pinRAF);
    this._pinRAF = null;
    clearTimeout(this._pinHardStop);
    clearTimeout(this._pinSettleTimer);
  }

  listenForVariantChanges() {
    // Listen to Shopify PubSub variantChange (fired by product-info.js)
    const onVariantChange = ({ data: { variant } }) => {
      // The re-render has landed — let the scroll pin absorb the final reflow
      // (price/gallery swap) for a few frames, then release scrolling.
      this._settleScrollPin();
      if (!variant) return;
      this.updateUI(variant.id);
      const isPreorder = this._isPreorderVariant(variant.id, variant.available);
      this._setStickyBtnMode(
        isPreorder,
        variant.available,
        variant.price,
        variant.id,
      );
      this._setTrayAtcBtnMode(
        isPreorder,
        variant.available,
        variant.price,
        variant.id,
      );
    };

    // Unavailable combos re-render without publishing variantChange, so settle
    // the pin here too — otherwise scrolling stays frozen until the hard cap.
    document.addEventListener("pdp:variant-unavailable", () =>
      this._settleScrollPin(),
    );

    if (
      typeof subscribe !== "undefined" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    } else {
      // PubSub not ready yet — wait for it
      const waitForPubSub = setInterval(() => {
        if (
          typeof subscribe !== "undefined" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          clearInterval(waitForPubSub);
          subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
        }
      }, 50);
    }

    // Legacy: listen to custom variant:change DOM event
    document.addEventListener("variant:change", (e) => {
      if (e.detail && e.detail.variant && e.detail.variant.id) {
        setTimeout(() => {
          this.attachEventListeners();
          this.updateUI(e.detail.variant.id);
        }, 100);
      }
    });
  }
}

// Register the custom element
customElements.define("sticky-nav-controller", StickyNavController);
